import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import API from '../utils/api';
import SEO from '../components/SEO';
import { trackReferralClaim, trackReferralReadAloud } from '../utils/analytics';
import LeadEmailForm from '../components/LeadEmailForm';

const SPEECH_LANG = {
  en:'en-GB', es:'es-ES', fr:'fr-FR', hi:'hi-IN',
  gu:'gu-IN', de:'de-DE', pt:'pt-BR', ar:'ar-SA',
  zh:'zh-CN', ja:'ja-JP', it:'it-IT',
};

const INTERESTS = ['Dinosaurs','Space','Animals','Magic','Superheroes','Princesses','Cars','Nature','Music','Ocean','Dragons','Robots'];

const TONES = [
  { value: 'magical',     icon: '✨', desc: 'Wands & wonder' },
  { value: 'adventurous', icon: '🗺️', desc: 'Epic quests' },
  { value: 'funny',       icon: '😄', desc: 'Giggles' },
  { value: 'calm',        icon: '🌙', desc: 'Peaceful' },
  { value: 'educational', icon: '📚', desc: 'Fun facts' },
  { value: 'cozy',        icon: '🔥', desc: 'Warm & comfy' },
  { value: 'exciting',    icon: '⚡', desc: 'Thrilling' },
  { value: 'spooky',      icon: '👻', desc: 'Mildly spooky' },
];

const LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
  { code: 'fr', label: 'French',     flag: '🇫🇷' },
  { code: 'hi', label: 'Hindi',      flag: '🇮🇳' },
  { code: 'gu', label: 'Gujarati',   flag: '🇮🇳' },
  { code: 'de', label: 'German',     flag: '🇩🇪' },
  { code: 'pt', label: 'Portuguese', flag: '🇧🇷' },
  { code: 'ar', label: 'Arabic',     flag: '🇸🇦' },
  { code: 'zh', label: 'Chinese',    flag: '🇨🇳' },
  { code: 'ja', label: 'Japanese',   flag: '🇯🇵' },
  { code: 'it', label: 'Italian',    flag: '🇮🇹' },
];

const THEME_CATEGORIES = [
  { label: 'Life Lessons', icon: '💡', themes: ['Kindness','Bravery','Friendship','Sharing','Honesty','Patience','Curiosity','Gratitude','Perseverance','Empathy','Courage','Generosity'] },
  { label: 'Seasonal',     icon: '🎃', themes: ['🎃 Halloween','🎄 Christmas','🌸 Spring','☀️ Summer','🍂 Autumn','❄️ Winter','🐣 Easter','🎆 New Year'] },
  { label: 'Emotional',    icon: '💛', themes: ['New Sibling','Starting School','Fear of the Dark','Moving Home','Missing Someone','Making New Friends','Feeling Nervous','Losing a Pet','Dealing with Anger','Being Different'] },
];

const LENGTH_OPTIONS = [
  { value: 'short',  label: 'Short',  icon: '⚡', desc: '~5 min' },
  { value: 'medium', label: 'Medium', icon: '📖', desc: '~8 min' },
  { value: 'long',   label: 'Long',   icon: '📚', desc: '~12 min' },
];

const LEARNING_CATEGORIES = [
  { label: 'Science & Nature',    icon: '🔬', topics: ['The Solar System','How Plants Grow','The Water Cycle','Volcanoes','Dinosaurs','Animals & Habitats','Weather & Seasons','The Human Body','Insects & Bugs','Ocean Life'] },
  { label: 'Maths',               icon: '🔢', topics: ['Counting & Numbers','Addition & Subtraction','Shapes & Geometry','Fractions','Telling Time','Measuring & Sizes','Patterns','Money & Coins'] },
  { label: 'Life Skills',         icon: '🌱', topics: ['Sharing & Taking Turns','Saying Sorry','Tidying Up','Washing Hands','Healthy Eating','Bedtime Routines','Road Safety','Stranger Danger','Managing Feelings'] },
  { label: 'History & Culture',   icon: '🏛️', topics: ['Ancient Egypt','Knights & Castles','Space Exploration','Famous Inventors','World Cultures','How Rainbows Form','Recycling & the Planet','Where Food Comes From'] },
  { label: 'Language & Literacy', icon: '📖', topics: ['Rhyming Words','The Alphabet','Opposites','Colours & Shades','Days of the Week','Months of the Year','Storytelling Structure'] },
];

// iOS Safari's speechSynthesis.pause() is broken — cancel+restart at last position instead.
const isIOSSpeech = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const ReferralPage = () => {
  const { token } = useParams();

  // Meta state
  const [meta, setMeta]     = useState(null);  // { referrerName, claimed, story }
  const [metaStatus, setMetaStatus] = useState('loading'); // loading | ready | claimed | expired | invalid

  // Form — basic
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge]   = useState('');
  const [interests, setInterests] = useState([]);
  const [formError, setFormError] = useState('');

  // Form — story preferences
  const [childGender, setChildGender]   = useState('neutral');
  const [language, setLanguage]         = useState('en');
  const [themes, setThemes]             = useState([]);
  const [tone, setTone]                 = useState('');
  const [storyLength, setStoryLength]   = useState('medium');
  const [learningTopic, setLearningTopic] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeThemeTab, setActiveThemeTab]       = useState(0);
  const [activeLearningTab, setActiveLearningTab] = useState(0);

  const toggleTheme = (t) =>
    setThemes(prev => prev.includes(t)
      ? prev.filter(x => x !== t)
      : prev.length < 5 ? [...prev, t] : prev
    );

  // Claim flow
  const [claiming, setClaiming]   = useState(false);
  const [generating, setGenerating] = useState(false); // background generation in progress
  const [story, setStory]         = useState(null); // generated story
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  // Surfaced when generation never completes. Without a bound the page span
  // forever and kept hitting a public endpoint every 3s for the life of the tab.
  const [genTimedOut, setGenTimedOut] = useState(false);
  const [fontSize, setFontSize]   = useState(18);
  const [speaking, setSpeaking]   = useState(false);
  const [speechError, setSpeechError] = useState(null);
  const utteranceRef = useRef(null);
  const resumeIntervalRef = useRef(null);
  const voicesRef = useRef([]);
  const [speechPaused, setSpeechPaused] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [activeParagraph, setActiveParagraph] = useState(-1);
  const speechParaPosRef = useRef([]);
  const speechLastCharRef = useRef(0);
  const speechTextOffsetRef = useRef(0);
  const speechFullTextRef = useRef('');

  // Pre-load voices so they're available synchronously when user taps (mobile requirement)
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const stopSpeech = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
    setSpeaking(false);
    setSpeechPaused(false);
    setActiveParagraph(-1);
    speechLastCharRef.current = 0;
    speechTextOffsetRef.current = 0;
  };

  // Cancel speech on unmount or page refresh
  useEffect(() => {
    window.addEventListener('beforeunload', stopSpeech);
    return () => { stopSpeech(); window.removeEventListener('beforeunload', stopSpeech); };
  }, []);

  const handleReadAloud = () => {
    if (!window.speechSynthesis) return;

    // ── Pause ─────────────────────────────────────────────────────────────────
    if (speaking && !speechPaused) {
      if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
      if (isIOSSpeech()) {
        window.speechSynthesis.cancel(); // iOS: pause() broken — cancel and track position
      } else {
        window.speechSynthesis.pause();
      }
      setSpeechPaused(true);
      return;
    }

    // ── Non-iOS resume (native browser API) ────────────────────────────────────
    if (speaking && speechPaused && !isIOSSpeech()) {
      window.speechSynthesis.resume();
      setSpeechPaused(false);
      resumeIntervalRef.current = setInterval(() => {
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      }, 10000);
      return;
    }

    // ── Fresh start OR iOS resume — both create a new utterance ───────────────
    const langCode = story.language || 'en';
    const bcp47 = SPEECH_LANG[langCode] || langCode;
    const langPrefix = bcp47.split('-')[0];
    const lesson = story.moralLesson ? ` Tonight's lesson: ${story.moralLesson}` : '';
    const fullText = (story.title + '. ' + story.content + lesson)
      .replace(/\*\*|__|\*|_/g, '').replace(/\n\n+/g, '. ')
      .replace(/\n/g, ' ').replace(/\.\.+/g, '.').replace(/\s{2,}/g, ' ')
      .replace(/\s+([.,!?])/g, '$1').trim();

    const LANG_NAMES = { en:'English', es:'Spanish', fr:'French', hi:'Hindi', gu:'Gujarati', de:'German', pt:'Portuguese', ar:'Arabic', zh:'Chinese', ja:'Japanese', it:'Italian' };
    const voices = voicesRef.current;
    const voice = voices.length
      ? (voices.find(v => v.lang === bcp47 && v.localService) ||
         voices.find(v => v.lang === bcp47) ||
         voices.find(v => v.lang.startsWith(langPrefix)) ||
         (langCode === 'en' ? voices.find(v => v.lang.startsWith('en')) : null))
      : null;

    if (!voice && langCode !== 'en') {
      const langName = LANG_NAMES[langCode] || langCode.toUpperCase();
      setSpeechError(`Your browser has no ${langName} voice installed. Try on a mobile device for ${langName} read-aloud.`);
      return;
    }
    setSpeechError(null);

    const isFresh = !(speaking && speechPaused);
    const offset = isFresh ? 0 : speechLastCharRef.current;
    const text = offset > 0 && speechFullTextRef.current ? speechFullTextRef.current.slice(offset) : fullText;

    if (isFresh) {
      speechFullTextRef.current = fullText;
      speechLastCharRef.current = 0;
      speechTextOffsetRef.current = 0;
      const paragraphs = story.content?.split('\n\n') || [];
      const titlePart = story.title + '. ';
      let pos = titlePart.length;
      speechParaPosRef.current = paragraphs.map(p => {
        const start = pos; pos += p.replace(/\*\*|__|\*|_/g, '').length + 2; return start;
      });
      trackReferralReadAloud(langCode);
    }
    speechTextOffsetRef.current = offset;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = bcp47; utterance.rate = playbackSpeed * 0.85; utterance.pitch = 1.05;
    if (voice) utterance.voice = voice;

    utterance.onboundary = (e) => {
      if (e.name !== 'word' && e.name !== 'sentence') return;
      const abs = e.charIndex + speechTextOffsetRef.current;
      speechLastCharRef.current = abs;
      const starts = speechParaPosRef.current;
      let active = -1;
      for (let i = 0; i < starts.length; i++) { if (abs >= starts[i]) active = i; else break; }
      setActiveParagraph(active);
    };
    utterance.onend = () => {
      if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
      setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
      speechLastCharRef.current = 0; speechTextOffsetRef.current = 0;
    };
    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') {
        if (resumeIntervalRef.current) { clearInterval(resumeIntervalRef.current); resumeIntervalRef.current = null; }
        setSpeaking(false); setSpeechPaused(false); setActiveParagraph(-1);
      }
    };
    utteranceRef.current = utterance;
    // Do NOT call cancel() before speak() — on Android Chrome it silently cancels the new utterance
    window.speechSynthesis.speak(utterance);
    // iOS Safari pauses speech after ~15s — keep it alive with periodic resume
    resumeIntervalRef.current = setInterval(() => {
      if (window.speechSynthesis.paused && !speechPaused) window.speechSynthesis.resume();
    }, 10000);
    setSpeaking(true); setSpeechPaused(false);
  };

  // Start polling GET /referral/:token every 3s until story is ready
  // Bounded at 3 minutes. Background generation writes no failure state to the
  // Referral doc — its catch only logs to Sentry — so if the AI call or the
  // illustration throws, the story simply never appears. Polling forever meant an
  // endless spinner for the recipient AND a request every 3 seconds against a
  // public, unauthenticated endpoint for as long as the tab stayed open.
  // CheckoutSuccessPage already bounds its equivalent poll.
  const MAX_POLLS = 60; // 60 x 3s = 3 minutes, generous for story + illustration

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (tok) => {
    if (pollRef.current) return; // already polling
    pollCountRef.current = 0;
    setGenTimedOut(false);
    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      try {
        const res = await API.get(`/referral/${tok}`);
        if (res.data.story) {
          stopPolling();
          setGenerating(false);
          setStory(res.data.story);
          setMetaStatus('claimed');
          trackReferralClaim();
          return;
        }
        // If still generating, keep polling
      } catch {
        // Network hiccup — keep polling until the attempt cap
      }
      if (pollCountRef.current >= MAX_POLLS) {
        stopPolling();
        setGenerating(false);
        setGenTimedOut(true);
      }
    }, 3000);
  };

  // Stop polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    API.get(`/referral/${token}`)
      .then(res => {
        setMeta(res.data);
        if (res.data.story) {
          setStory(res.data.story);
          setMetaStatus('claimed');
        } else if (res.data.generating) {
          // Claimed but generation still running (e.g. page refreshed mid-gen)
          setGenerating(true);
          setMetaStatus('ready');
          startPolling(token);
        } else {
          setMetaStatus('ready');
        }
      })
      .catch(err => {
        const status = err.response?.status;
        setMetaStatus(status === 410 ? 'expired' : 'invalid');
      });
  }, [token]); // eslint-disable-line

  const toggleInterest = (i) =>
    setInterests(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);

  const handleClaim = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!childName.trim()) return setFormError('Please enter your child\'s name.');
    if (!childAge || childAge < 1 || childAge > 12) return setFormError('Please enter a valid age between 1 and 12.');

    setClaiming(true);
    try {
      const res = await API.post(`/referral/${token}/claim`, {
        childName:    childName.trim(),
        childAge:     parseInt(childAge, 10),
        childGender,
        interests,
        language,
        themes,
        tone,
        storyLength,
        learningTopic: learningTopic.trim(),
        customPrompt:  customPrompt.trim(),
      });
      if (res.data.story) {
        // Already had a cached story (alreadyClaimed path)
        setStory(res.data.story);
        setMetaStatus('claimed');
        trackReferralClaim();
      } else if (res.data.generating) {
        // Story is being generated in the background — start polling
        setGenerating(true);
        startPolling(token);
      }
    } catch (err) {
      const status = err.response?.status;
      setFormError(
        status === 429
          ? err.response.data.message
          : err.response?.data?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setClaiming(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (metaStatus === 'loading') return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <Navbar />
      <div className="text-center py-5 mt-5">
        <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
      </div>
    </div>
  );

  // ── Invalid / expired ────────────────────────────────────────────────────
  if (metaStatus === 'invalid' || metaStatus === 'expired') return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container text-center py-5 mt-5" style={{ maxWidth: '480px' }}>
        <div style={{ fontSize: '60px', marginBottom: '16px' }}>{metaStatus === 'expired' ? '⏰' : '🔗'}</div>
        <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>
          {metaStatus === 'expired' ? 'This link has expired' : 'Invalid referral link'}
        </h4>
        <p className="text-muted mb-4">
          {metaStatus === 'expired'
            ? 'Referral links are valid for 30 days. Ask your friend to send you a new one!'
            : 'This referral link is not valid. Please check the link and try again.'}
        </p>
        <Link to="/register" className="btn btn-dream rounded-pill px-5">Create a Free Account</Link>
      </div>
    </div>
  );

  // ── Story ready to read ───────────────────────────────────────────────────
  if (story) return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO
        title={story.title}
        description={`A free personalised bedtime story for ${story.child?.name} — a gift from ${meta?.referrerName} via Dreamy Bedtime.`}
      />
      <Navbar />

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69 0%,#6f42c1 100%)', padding: '32px 0 24px' }}>
        <div className="container text-center">
          <p style={{ color: '#c9a8ff', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }}>
            🌙 DREAMY BEDTIME · FREE GIFT STORY
          </p>
          <h1 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(22px,4vw,36px)' }}>
            {story.title}
          </h1>
          <p style={{ color: '#c9a8ff', margin: 0, fontSize: '15px' }}>
            A free story for <strong style={{ color: '#fff' }}>{story.child?.name}</strong>, gifted by <strong style={{ color: '#fff' }}>{meta?.referrerName}</strong>
          </p>
        </div>
      </div>

      <div className="container py-4" style={{ maxWidth: '720px' }}>

        {/* Font size + read aloud controls */}
        <div className="card border-0 shadow-sm mb-4 p-3 d-flex flex-row align-items-center gap-2" style={{ borderRadius: '14px' }}>
          <button className="btn btn-sm btn-outline-secondary rounded-circle" onClick={() => setFontSize(f => Math.max(14, f - 2))} title="Smaller text">A-</button>
          <button className="btn btn-sm btn-outline-secondary rounded-circle" onClick={() => setFontSize(f => Math.min(28, f + 2))} title="Larger text">A+</button>
          <button
            className="btn btn-sm rounded-pill px-3 fw-semibold"
            onClick={handleReadAloud}
            style={(speaking || speechPaused) ? { background: '#6f42c1', color: '#fff', border: 'none' } : {}}
            title={speechPaused ? 'Resume reading' : speaking ? 'Pause reading' : 'Read aloud'}
          >
            <i className={`bi ${speechPaused ? 'bi-play-fill' : speaking ? 'bi-pause-fill' : 'bi-volume-up'} me-1`}></i>
            {speechPaused ? 'Resume' : speaking ? 'Pause' : 'Read Aloud'}
          </button>
          {(speaking || speechPaused) && (
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill px-3"
              onClick={stopSpeech}
              title="Stop reading"
            >
              <i className="bi bi-stop-fill me-1"></i>Stop
            </button>
          )}
          {(speaking || speechPaused) && (
            <div className="d-flex gap-1 ms-1">
              {[0.75, 1, 1.25].map(s => (
                <button key={s} onClick={() => setPlaybackSpeed(s)}
                  className="btn btn-sm rounded-pill"
                  style={{ padding: '2px 8px', fontSize: '11px',
                    background: playbackSpeed === s ? '#6f42c1' : '#f0e8ff',
                    color: playbackSpeed === s ? '#fff' : '#6f42c1',
                    border: '1px solid #c9a8ff', lineHeight: 1.4 }}>
                  {s}x
                </button>
              ))}
            </div>
          )}
        </div>

        {speechError && (
          <div className="d-flex align-items-center gap-2 p-3 mt-2 rounded-3" style={{ background: '#fff8ed', border: '1px solid #fcd34d', fontSize: '13px', color: '#92400e' }}>
            <i className="bi bi-info-circle-fill" style={{ flexShrink: 0 }}></i>
            <span style={{ flex: 1 }}>{speechError}</span>
            <button onClick={() => setSpeechError(null)} style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>
        )}

        {/* Story card */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden' }}>
          <div style={{ height: '6px', background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }} />
          <div className="p-4 p-lg-5">

            {story.illustrationUrl && (
              <div className="mx-auto mb-5" style={{ maxWidth: '480px' }}>
                <img src={story.illustrationUrl} alt={story.title}
                  style={{ width: '100%', borderRadius: '16px', display: 'block', boxShadow: '0 8px 32px rgba(111,66,193,0.18)' }} />
              </div>
            )}

            <div style={{ lineHeight: '1.9', fontSize: `${fontSize}px`, color: 'var(--text-main)' }}>
              {story.content?.split('\n\n').map((para, i) => (
                <p key={i} className="mb-4" style={i === activeParagraph ? {
                  background: 'rgba(111,66,193,0.09)',
                  borderLeft: '3px solid #6f42c1',
                  paddingLeft: '10px',
                  borderRadius: '4px',
                  transition: 'background 0.3s',
                } : { paddingLeft: '0', borderLeft: 'none' }}>{para}</p>
              ))}
            </div>

            {story.moralLesson && (
              <div className="mt-5 p-4 text-center rounded-3" style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border: '2px dashed #c9a8ff' }}>
                <div className="fs-2 mb-2">💫</div>
                <p className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Tonight's lesson</p>
                <p className="mb-0 fst-italic" style={{ color: '#6f42c1', fontSize: '17px' }}>"{story.moralLesson}"</p>
              </div>
            )}
          </div>
          <div style={{ height: '6px', background: 'linear-gradient(135deg,#6f42c1,#2d1b69)' }} />
        </div>

        {/* Save-by-email lead capture */}
        <div className="card border-0 shadow-sm mt-4 p-4 p-lg-5 text-center" style={{ borderRadius: '24px', background: 'var(--card-bg)', border: '1.5px solid var(--border-purple)' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>📬</div>
          <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            Want to save this story?
          </h5>
          <p style={{ color: 'var(--text-muted2)', fontSize: '14px', maxWidth: '420px', margin: '0 auto 18px' }}>
            Enter your email and we'll send {story.child?.name ? `${story.child?.name}'s` : 'this'} story to your inbox — yours to keep forever.
          </p>
          <LeadEmailForm
            source="referral"
            extraBody={{ referralToken: token }}
            askName={false}
            buttonLabel="📬 Save my story"
          />
        </div>

        {/* CTA */}
        <div className="card border-0 shadow-sm mt-4 p-4 p-lg-5 text-center" style={{ borderRadius: '24px', background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>🌙</div>
          <h5 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
            Want more magical stories like this?
          </h5>
          <p style={{ color: '#c9a8ff', fontSize: '15px', maxWidth: '400px', margin: '0 auto 20px' }}>
            Dreamy Bedtime creates personalised bedtime stories for your children every single night. Free to start.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold px-5">
              Start Free — 5 Stories a Month
            </Link>
            <Link to="/pricing" className="btn btn-outline-light rounded-pill px-4">
              See Plans
            </Link>
          </div>
          <p style={{ color: '#a080d8', fontSize: '12px', marginTop: '12px', marginBottom: 0 }}>
            No credit card required · Cancel anytime
          </p>
        </div>

      </div>
    </div>
  );

  // ── Form to claim ────────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO
        title={`${meta?.referrerName} wants to give your child a free bedtime story`}
        description="Claim your free personalised bedtime story — a gift from a friend on Dreamy Bedtime."
      />
      <Navbar />

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69 0%,#6f42c1 100%)', padding: '40px 0 32px' }}>
        <div className="container text-center" style={{ maxWidth: '600px' }}>
          <p style={{ color: '#c9a8ff', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>
            🌙 DREAMY BEDTIME
          </p>
          <div style={{ fontSize: '56px', marginBottom: '12px' }}>🎁</div>
          <h1 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,5vw,38px)' }}>
            You've been gifted a free bedtime story!
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '16px', margin: 0 }}>
            <strong style={{ color: '#fff' }}>{meta?.referrerName}</strong> thinks your child deserves a magical night.
            <br />Tell us about your child and we'll write one just for them — free, right now.
          </p>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: '560px' }}>
        <div className="card border-0 shadow-sm" style={{ borderRadius: '24px', overflow: 'hidden' }}>
          <div style={{ height: '5px', background: 'linear-gradient(90deg,#6f42c1,#c9a8ff)' }} />
          <div className="p-4 p-lg-5">
            <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Tell us about your child</h5>
            <p className="text-muted small mb-4">We'll write a personalised story starring them — completely free.</p>

            {formError && (
              <div className="alert alert-danger py-2 small rounded-3 mb-3">{formError}</div>
            )}

            <form onSubmit={handleClaim}>
              {/* Child name */}
              <div className="mb-3">
                <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Child's first name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  placeholder="e.g. Emma"
                  value={childName}
                  onChange={e => setChildName(e.target.value)}
                  maxLength={50}
                  required
                />
              </div>

              {/* Age */}
              <div className="mb-3">
                <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Age <span className="text-danger">*</span></label>
                <select
                  className="form-select rounded-3"
                  value={childAge}
                  onChange={e => setChildAge(e.target.value)}
                  required
                >
                  <option value="">Select age…</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(a => (
                    <option key={a} value={a}>{a} year{a !== 1 ? 's' : ''} old</option>
                  ))}
                </select>
              </div>

              {/* Gender */}
              <div className="mb-3">
                <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Gender</label>
                <div className="d-flex gap-2">
                  {[
                    { value: 'boy',     label: 'Boy',     icon: '👦' },
                    { value: 'girl',    label: 'Girl',    icon: '👧' },
                    { value: 'neutral', label: 'Neutral', icon: '🧒' },
                  ].map(g => (
                    <button key={g.value} type="button"
                      className={`btn btn-sm rounded-pill px-3 ${childGender === g.value ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => setChildGender(g.value)}>
                      {g.icon} {g.label}
                    </button>
                  ))}
                </div>
                <div className="form-text">Helps the story use the right pronouns.</div>
              </div>

              {/* Interests */}
              <div className="mb-4">
                <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                  What does {childName || 'your child'} love? <span className="text-muted fw-normal">(optional)</span>
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {INTERESTS.map(i => (
                    <button
                      key={i} type="button"
                      onClick={() => toggleInterest(i)}
                      className={`btn btn-sm rounded-pill ${interests.includes(i) ? 'btn-dream' : 'btn-outline-secondary'}`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>

              {/* Advanced / customise toggle */}
              <button
                type="button"
                className="btn btn-sm w-100 mb-3 rounded-pill fw-semibold"
                style={{ background: 'var(--purple-section)', color: '#6f42c1', border: '1.5px solid #d4b8ff' }}
                onClick={() => setShowAdvanced(v => !v)}
              >
                <i className={`bi ${showAdvanced ? 'bi-chevron-up' : 'bi-sliders'} me-2`}></i>
                {showAdvanced ? 'Hide story options' : 'Customise the story ✨'}
              </button>

              {showAdvanced && (
                <div className="mb-3 p-3 rounded-3" style={{ background: '#faf7ff', border: '1px solid #e0d4f7' }}>

                  {/* Story language */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Story language</label>
                    <div className="d-flex flex-wrap gap-2">
                      {LANGUAGES.map(lang => (
                        <button key={lang.code} type="button"
                          className={`btn btn-sm rounded-pill ${language === lang.code ? 'btn-dream' : 'btn-outline-secondary'}`}
                          onClick={() => setLanguage(lang.code)}>
                          {lang.flag} {lang.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Themes */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Themes <span className="text-muted fw-normal">(pick up to 5)</span></label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {THEME_CATEGORIES.map((cat, i) => (
                        <button key={i} type="button"
                          className={`btn btn-sm rounded-pill ${activeThemeTab === i ? 'btn-dream' : 'btn-outline-secondary'}`}
                          onClick={() => setActiveThemeTab(i)}>
                          {cat.icon} {cat.label}
                        </button>
                      ))}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {THEME_CATEGORIES[activeThemeTab].themes.map(t => (
                        <button key={t} type="button"
                          className={`btn btn-sm rounded-pill ${themes.includes(t) ? 'btn-dream' : 'btn-outline-secondary'}`}
                          onClick={() => toggleTheme(t)}
                          disabled={!themes.includes(t) && themes.length >= 5}>
                          {t}
                        </button>
                      ))}
                    </div>
                    {themes.length > 0 && (
                      <div className="mt-1 small text-muted">Selected: {themes.join(' · ')}</div>
                    )}
                  </div>

                  {/* Tone */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Story tone</label>
                    <div className="row g-2">
                      {TONES.map(t => (
                        <div className="col-6 col-sm-3" key={t.value}>
                          <div onClick={() => setTone(prev => prev === t.value ? '' : t.value)}
                            style={{
                              border: `2px solid ${tone === t.value ? '#6f42c1' : '#e0d4f7'}`,
                              borderRadius: '12px', padding: '8px 6px', cursor: 'pointer', textAlign: 'center',
                              background: tone === t.value ? '#f0e8ff' : '#fff', transition: 'all 0.15s',
                            }}>
                            <div style={{ fontSize: '18px', marginBottom: '2px' }}>{t.icon}</div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-main)', textTransform: 'capitalize' }}>{t.value}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-soft)' }}>{t.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Story length */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Story length</label>
                    <div className="row g-2">
                      {LENGTH_OPTIONS.map(o => (
                        <div className="col-4" key={o.value}>
                          <div onClick={() => setStoryLength(o.value)}
                            style={{
                              border: `2px solid ${storyLength === o.value ? '#6f42c1' : '#e0d4f7'}`,
                              borderRadius: '12px', padding: '10px 6px', cursor: 'pointer', textAlign: 'center',
                              background: storyLength === o.value ? '#f0e8ff' : '#fff', transition: 'all 0.15s',
                            }}>
                            <div style={{ fontSize: '18px', marginBottom: '2px' }}>{o.icon}</div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-main)' }}>{o.label}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-soft)' }}>{o.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Learning topic */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Learning topic <span className="text-muted fw-normal">(optional)</span></label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {LEARNING_CATEGORIES.map((cat, i) => (
                        <button key={i} type="button"
                          className={`btn btn-sm rounded-pill ${activeLearningTab === i ? 'btn-dream' : 'btn-outline-secondary'}`}
                          onClick={() => setActiveLearningTab(i)}>
                          {cat.icon} {cat.label}
                        </button>
                      ))}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {LEARNING_CATEGORIES[activeLearningTab].topics.map(topic => (
                        <button key={topic} type="button"
                          className={`btn btn-sm rounded-pill ${learningTopic === topic ? 'btn-dream' : 'btn-outline-secondary'}`}
                          onClick={() => setLearningTopic(prev => prev === topic ? '' : topic)}>
                          {topic}
                        </button>
                      ))}
                    </div>
                    <input className="form-control form-control-sm mt-1" placeholder="Or type your own topic…"
                      maxLength={200}
                      value={learningTopic}
                      onChange={e => setLearningTopic(e.target.value)} />
                  </div>

                  {/* Special request */}
                  <div>
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>Special request <span className="text-muted fw-normal">(optional)</span></label>
                    <textarea className="form-control" rows={2}
                      placeholder={`e.g. Make ${childName || 'the child'} find a dragon egg at the end`}
                      maxLength={500}
                      value={customPrompt}
                      onChange={e => setCustomPrompt(e.target.value)} />
                    <div className="form-text text-end">{customPrompt.length}/500</div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={claiming || generating}
                className="btn btn-dream w-100 rounded-pill fw-bold py-3"
                style={{ fontSize: '16px' }}
              >
                {claiming ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Sending to our writers…</>
                ) : (
                  '✨ Generate My Free Story'
                )}
              </button>
            </form>

            {generating && (
              <div className="text-center mt-4 p-4 rounded-3" style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border: '1px solid #c9a8ff' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🌙</div>
                <div className="spinner-border mb-3" style={{ color: '#6f42c1', width: '2rem', height: '2rem' }}></div>
                <p className="fw-semibold mb-1" style={{ color: 'var(--text-main)' }}>
                  Crafting a magical story for {childName || 'your child'}…
                </p>
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                  This usually takes 20–30 seconds. You don't need to stay on this page —
                  the story will be here when you come back.
                </p>
              </div>
            )}

            {/* Generation never completed within the poll window. Say so and
                offer a retry instead of leaving a spinner that never resolves. */}
            {genTimedOut && (
              <div className="text-center mt-4 p-4 rounded-3" style={{ background: 'var(--yellow-section)', border: '1px solid var(--border-yellow)' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                <p className="fw-semibold mb-1" style={{ color: 'var(--text-orange2)' }}>
                  This story is taking longer than expected
                </p>
                <p className="mb-3" style={{ color: 'var(--text-orange2)', fontSize: '13px', opacity: 0.9 }}>
                  Your gift is safe — it may still be finishing. Refresh in a moment to check,
                  or email support@dreamybedtime.com and we'll sort it out.
                </p>
                <button
                  className="btn btn-sm btn-outline-secondary rounded-pill px-4"
                  onClick={() => { setGenTimedOut(false); setGenerating(true); startPolling(token); }}
                >
                  Check again
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-4">
          <p style={{ color: 'var(--text-soft)', fontSize: '13px' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#6f42c1', fontWeight: 600 }}>Sign in</Link>
            {' '}to access your full story library.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
